import React, { useEffect, useRef, useState, useContext } from 'react';
import Chart from 'chart.js/auto';
import { ApiContext } from '../../../../../context/MyContext';
import * as userService from '../../../../../service/userService';
import WeightGraphModal from '../DataGraphs/WeightGraphModal';
import Button from '../../../../../common/Button/Button';
import { parseLocalDate } from '../../../../../utils/formatLocalDate';
const WeightChart = ({ profileId, selectedDate }) => {
    const chartRef = useRef(null);
    const { get } = useContext(ApiContext);
    const [weightData, setWeightData] = useState([]);
    const [labels, setLabels] = useState([]);
    const [currentWeight, setCurrentWeight] = useState(0);
    const [targetWeight, setTargetWeight] = useState(0);
    const [hasData, setHasData] = useState(true);
    const [showModal,setShowModal] = useState(false);
    useEffect(() => {
      const fetchWeight = async () => {
        const result = await userService.fetchWeightSummary(get, profileId, 'W', selectedDate);
        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
            const values = result.data.map(d => d.value);
            const labels = result.data.map(d => {
              const date = parseLocalDate(d.date);
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            });
            const last = result.data[result.data.length - 1];
          
            setWeightData(values);
            setLabels(labels);
            setCurrentWeight(last.value);
            setTargetWeight(last?.recommended ?? 0);
            setHasData(true);
          } else {
            setHasData(false);
          }          
      };
      fetchWeight();
    }, [profileId, selectedDate]);
  
    useEffect(() => {
      if (!chartRef.current || !weightData.length) return;
  
      const chartWeightData = [...weightData];
      const minVal = Math.min(...chartWeightData, targetWeight);
      const maxVal = Math.max(...chartWeightData, targetWeight);
      const buffer = 2;
      const minY = Math.floor(minVal - buffer);
      const maxY = Math.ceil(maxVal + buffer);
  
      const chart = new Chart(chartRef.current, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Weight (kg)',
            data: chartWeightData,
            borderColor: '#FF6414',
            backgroundColor: '#FF6414',
            borderWidth: 4,
            tension: 0.3,
            fill: false,
            pointRadius: ctx => ctx.dataIndex === weightData.length - 1 ? 4 : 0,
            pointBackgroundColor: ctx => ctx.dataIndex === weightData.length - 1 ? '#FF6414' : 'transparent'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          aspectRatio: 1.3,
          layout: { padding: { top: 10, bottom: 20, left: 20, right: 10 } },
          scales: {
            x: {
              ticks: {
                color: ctx => ctx.index === ctx.chart.data.labels.length - 1 ? '#FF6414' : '#9CA3AF',
                font: { size: 12, weight: 'bold' }
              },
              grid: { display: false, drawBorder: false }
            },
            y: {
              min: minY,
              max: maxY,
              ticks: {
                callback: value => Number.isInteger(value) ? `${value} Kg` : '',
                font: { size: 12 },
                color: '#6B7280',
                padding: 6
              },
              grid: { color: '#E5E7EB', drawTicks: true, drawBorder: false }
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: context => `Weight: ${context.raw} Kg`
              }
            }
          },
          elements: { line: { borderWidth: 4 } }
        },
        plugins: [
          {
            id: 'weightAnnotations',
            afterDraw(chart) {
              const { ctx, chartArea: { left, right }, scales } = chart;
              if (!chartWeightData.length) return;
  
              const startWeight = chartWeightData[0] ?? currentWeight;
              const current = chartWeightData[chartWeightData.length - 1] ?? currentWeight;
              const goalWeight = targetWeight ?? chartWeightData[chartWeightData.length - 1];
  
              const startY = scales.y.getPixelForValue(startWeight);
              const currentY = scales.y.getPixelForValue(current);
              const goalY = scales.y.getPixelForValue(goalWeight);
  
              ctx.save();
              ctx.fillStyle = '#FF6414';
              ctx.font = '12px sans-serif';
              const labelOffset = 6;
              const usedY = new Set();
  
              function drawLabel(text, y) {
                let finalY = y;
                while (usedY.has(finalY)) finalY -= 12;
                ctx.fillText(text, left + 5, finalY - labelOffset);
                usedY.add(finalY);
              }
  
              drawLabel(`Goal: ${goalWeight.toFixed(1)} Kg`, goalY);
              drawLabel(`Current: ${current.toFixed(1)} Kg`, currentY);
              drawLabel(`Start: ${startWeight.toFixed(1)} Kg`, startY);
  
              ctx.strokeStyle = '#FF641422';
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.moveTo(left, goalY);
              ctx.lineTo(right, goalY);
              ctx.stroke();
  
              ctx.strokeStyle = '#FF6414';
              ctx.shadowColor = 'rgba(255, 100, 20, 0.5)';
              ctx.shadowBlur = 10;
              ctx.setLineDash([5, 5]);
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.moveTo(left, currentY);
              ctx.lineTo(right, currentY);
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.shadowBlur = 0;
  
              ctx.restore();
            }
          }
        ]
      });
  
      return () => chart.destroy();
    }, [weightData, targetWeight]);
  
    return (
        <div className="bg-white rounded-xl shadow-md w-[260px] h-[360px] flex flex-col items-center justify-center text-center p-4">
    <div className="text-xl font-semibold leading-tight"> Weight Analytics </div>
    {hasData ? (
      <>
        <div className="flex-1 w-full">
          <canvas ref={chartRef} className="w-full h-full" />
        </div>
        <div className="py-2">
          <p className="text-sm font-semibold leading-tight">
            Weight<br />{currentWeight} kg
          </p>
        </div>
       

      </>
    ) : (
      <p className="text-sm text-gray-500 font-medium">
        No weight data available<br />for this week
      </p>
    )}
     <div>
      <Button  
       text = "View Trends" 
       onClick={() => setShowModal(true)} 
       className="bg-orange-primary hover:bg-dark-orange w-32"/>
      </div>
    {showModal && (
  <WeightGraphModal
    profileId={profileId}
    selectedDate={selectedDate}
    onClose={() => setShowModal(false)}
  />
)}
  </div>
    );
  };
  
  export default WeightChart;